import type { DefaultNamespace } from 'i18next'
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import { cn } from '@/helpers/utils'

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
	id?: string
	label?: ReactNode
	labelPosition?: 'left' | 'right'
	name?: string
	errorMessage?: string | DefaultNamespace
	helpText?: string | DefaultNamespace
	placeholder?: string
	containerClass?: string
	invisible?: boolean
}

const CHECKMARK_WHITE =
	"url(\"data:image/svg+xml,%3Csvg viewBox='-4 -4 18 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L4 7L9 1' stroke='%23FFFFFF' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"
const CHECKMARK_GRAY =
	"url(\"data:image/svg+xml,%3Csvg viewBox='-4 -4 18 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L4 7L9 1' stroke='%23D0D5DD' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"
const INDETERMINATE_WHITE =
	"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='6' y='12' width='12' height='1' stroke='%23FFFFFF' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	(
		{
			id,
			label,
			labelPosition = 'right',
			errorMessage,
			helpText,
			name,
			onChange,
			onBlur,
			containerClass,
			invisible,
			...rest
		},
		ref
	) => {
		return (
			<div
				className={cn(
					'flex',
					label ? 'gap-2' : 'gap-0',
					containerClass
				)}
			>
				<label
					className={cn(
						'flex w-max max-w-full gap-1',
						labelPosition === 'right'
							? 'flex-row'
							: 'flex-row-reverse'
					)}
				>
					<input
						className={cn(
							'relative flex h-5 w-5 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-[6px] bg-center bg-no-repeat transition-all duration-200',
							'border border-neutral-300 bg-neutral-white',
							'checked:border-neutral-600 checked:bg-neutral-600 checked:[background-image:var(--checkbox-checked)]',
							'disabled:checked:cursor-not-allowed disabled:checked:border-neutral-300 disabled:checked:bg-neutral-100 disabled:checked:[background-image:var(--checkbox-checked-disabled)]',
							'disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-100',
							'hover:enabled:border-neutral-300 hover:enabled:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),0_0_0_2px_var(--color-neutral-100)]',
							'indeterminate:border-neutral-600 indeterminate:bg-neutral-600 indeterminate:[background-image:var(--checkbox-indeterminate)]',
							invisible && 'invisible transition-none'
						)}
						name={name}
						onBlur={onBlur}
						onChange={onChange}
						ref={ref}
						style={
							{
								'--checkbox-checked': CHECKMARK_WHITE,
								'--checkbox-checked-disabled': CHECKMARK_GRAY,
								'--checkbox-indeterminate': INDETERMINATE_WHITE
							} as React.CSSProperties
						}
						type='checkbox'
						{...rest}
					/>
				</label>
				<div className='flex flex-col gap-0.5'>
					<label
						className='text-neutral-900 text-sm [&>a]:cursor-pointer [&>a]:underline'
						htmlFor={id}
					>
						{label}
					</label>
					<InputHelpText error={errorMessage} helpText={helpText} />
				</div>
			</div>
		)
	}
)

Checkbox.displayName = 'Checkbox'
